import type { ICommandBarItemProps, IIconProps } from '@fluentui/react';
import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { acceptIcon } from 'globalStyles';
import { TFunction } from 'i18next';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import {
  CopilotChatInputPlaceholder,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
  CopilotPanelRuntimeView,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { describeQuestionAnswers, parseQuestionAnswersJson, questionAnswersInput } from 'models/copilot/questionSet';
import {
  AutomationDraftingStage,
  automationDraftingCommandAccept,
  automationDraftingCommandTest,
  TenantProcessStateAutomationDrafting,
} from 'models/copilot/Processes/AutomationDrafting/tenantProcessStateAutomationDrafting';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import { apiCopilotExecuteProcessAutomationDrafting } from 'services/Api/copilotService';
import { ProgressHubProcess } from 'services/SignalR/ProgressHub';
import { getCopilotProcessWelcomeMessage } from '../copilotProcessMessages';
import { questionsCardAction } from './automationDraftingAction';
import { CopilotAutomationDraftingPanelState } from './copilotAutomationDraftingPanelState';

// The server's cards (AutomationDraftingStep): their submit action names and the inputs they answer with
const proposalCardAction = 'automationProposal';
const scheduleCardAction = 'automationSchedule';
const candidateInput = 'candidate';
const scheduleInput = 'schedule';
const freshChoice = 'fresh';

const annualPlanRoute = '/schedule/overview';

const testIcon: IIconProps = { iconName: 'TestBeaker' };

/**
 * The drafting process (phase 27) as the three definitions that list Draft automation drive it: one execute per
 * turn or stage action, the state kept in the panel's usage state, the draft shown in the usage preview with
 * Accept and Test, the chat opened only in the refinement stage, the cards answered through the block inputs. The
 * definitions branch to these on their own hooks; the shell stays generic.
 */
export class AutomationDraftingProcess {
  static isDraftingAction(action: CopilotAction | undefined): boolean {
    return action?.processId === TenantProcess.AutomationDrafting;
  }

  static isRunning(runtime: Pick<CopilotPanelRuntimeView, 'action'>): boolean {
    return (
      AutomationDraftingProcess.isDraftingAction(runtime.action) &&
      runtime.action?.status === TenantProcessStatus.InProgress
    );
  }

  static getPanelState(
    runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>,
  ): CopilotAutomationDraftingPanelState | undefined {
    return runtime.getUsageState<CopilotAutomationDraftingPanelState>(CopilotPanelContext.AutomationDrafting);
  }

  static getState(
    runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>,
  ): TenantProcessStateAutomationDrafting | undefined {
    return AutomationDraftingProcess.getPanelState(runtime)?.state;
  }

  static getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    return AutomationDraftingProcess.isDraftingAction(action) ? getCopilotProcessWelcomeMessage(action, t) : undefined;
  }

  // The server says per stage whether typing is allowed (refinement only); before its first answer nothing is.
  static canStartChat(runtime: CopilotPanelRuntimeView): boolean {
    if (!AutomationDraftingProcess.isRunning(runtime)) return false;

    return AutomationDraftingProcess.getState(runtime)?.inputAllowed === true;
  }

  static getChatInputPlaceholder(runtime: CopilotPanelRuntimeView): CopilotChatInputPlaceholder | undefined {
    if (!AutomationDraftingProcess.isRunning(runtime)) return undefined;

    switch (AutomationDraftingProcess.getState(runtime)?.stage) {
      case AutomationDraftingStage.Selection:
      case AutomationDraftingStage.Scheduling:
        return CopilotChatInputPlaceholder.AnswerCard;
      default:
        return undefined;
    }
  }

  // The proposal is the long step with a progress bar; it answers in the same request, so a terminal progress
  // push must not reload the persisted state over the answer. The annual plan start has no item, hence no bar.
  static getProgressRef(
    runtime: CopilotPanelRuntimeView,
    entityRef: CopilotPanelEntityRef | undefined,
  ): CopilotPanelProgressRef | undefined {
    if (!AutomationDraftingProcess.isRunning(runtime)) return undefined;
    if (!entityRef || entityRef.entityType === EntityTypes.NotSet || !entityRef.entityId) return undefined;

    const state = AutomationDraftingProcess.getState(runtime);
    if (state !== undefined && state.stage !== AutomationDraftingStage.Proposal) return undefined;

    return {
      ...entityRef,
      tenantProcess: TenantProcess.AutomationDrafting,
      hubProcess: ProgressHubProcess.AutomationDrafting,
      // A live start answers in its own request (no state yet): the terminal push must not reload over that
      // answer. A run restored mid-proposal (a reload: the state is there, still at Proposal) has no request to
      // answer in, so the terminal push IS the signal to load the candidates card.
      completesInRequest: state === undefined,
    };
  }

  // Accept and Test over the draft, as the server's stage offers them (state.actions); each queues its command
  // and submits the conversation, so the execute of this turn carries it.
  static getPreviewCommands(runtime: CopilotPanelRuntime, t: TFunction<string[]>): ICommandBarItemProps[] {
    if (!AutomationDraftingProcess.isRunning(runtime)) return [];

    const panelState = AutomationDraftingProcess.getPanelState(runtime);
    const actions = panelState?.state?.actions ?? [];
    if (!panelState || actions.length === 0) return [];

    // the command is consumed by the execute of this submit; a submit that fails before the execute (no hub
    // connection, no token) must not leave it for the next typed turn
    const run = (command: string) => {
      panelState.queueCommand(command);
      void runtime.submitConversation(runtime.conversation).finally(() => panelState.takeCommand());
    };

    const items: ICommandBarItemProps[] = [];
    if (actions.includes(automationDraftingCommandAccept)) {
      items.push({
        key: automationDraftingCommandAccept,
        text: t('copilot:AutomationDrafting.Accept'),
        iconProps: acceptIcon,
        onClick: () => run(automationDraftingCommandAccept),
      });
    }
    if (actions.includes(automationDraftingCommandTest)) {
      items.push({
        key: automationDraftingCommandTest,
        text: t('copilot:AutomationDrafting.Test'),
        iconProps: testIcon,
        onClick: () => run(automationDraftingCommandTest),
      });
    }

    return items;
  }

  /**
   * One execute: a start, a typed turn, a card answer or a queued stage command. The answer replaces the panel's
   * state and conversation; a draft opens the usage preview. Completed or Failed ends the process in the panel;
   * a wired check is published to the annual plan, which the panel navigates to from any other scene.
   */
  static async execute(runtime: CopilotPanelRuntime, entityRef: CopilotPanelEntityRef | undefined): Promise<void> {
    const { action } = runtime;
    if (!action || !AutomationDraftingProcess.isDraftingAction(action)) return;

    const panelState = AutomationDraftingProcess.getPanelState(runtime);
    const command = panelState?.takeCommand();

    runtime.setIsLoading(true);
    const answer = await apiCopilotExecuteProcessAutomationDrafting(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      entityRef?.entityType ?? EntityTypes.NotSet,
      entityRef?.entityId ?? 0,
      command,
      runtime.connectionId,
      runtime.accessToken,
    );
    if (!answer) return;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
    panelState?.setState(answer);
    runtime.setPreviewContent(answer.draft?.proposal ? CopilotPreviewContent.Usage : CopilotPreviewContent.None);

    if (answer.status !== TenantProcessStatus.Completed && answer.status !== TenantProcessStatus.Failed) return;

    await runtime.endProcess(TenantProcess.AutomationDrafting);
    if (answer.status !== TenantProcessStatus.Completed || answer.wire?.wired !== true) return;

    runtime.resultBus.publishExternalCheckWired(answer.wire.integrationId);
    if (runtime.content?.context !== CopilotPanelContext.AnnualPlan) {
      runtime.appContext.copilotBridge.requestNavigation(annualPlanRoute);
    }
  }

  /**
   * The candidates card and the schedule card are this process's own: the choice goes onto the block of this
   * runtime's conversation (the server reads it there), a user bubble words it, and the panel runs the execute.
   */
  static onAdaptiveCardSubmit(
    runtime: CopilotPanelRuntime,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
    t: TFunction<string[]>,
  ): boolean {
    const submitted = payload.actionData.action;
    if (submitted !== proposalCardAction && submitted !== scheduleCardAction && submitted !== questionsCardAction)
      return false;
    if (!AutomationDraftingProcess.isRunning(runtime)) return false;

    AutomationDraftingProcess.applySubmittedInputs(runtime, block.id, payload.inputs);

    const wording =
      submitted === questionsCardAction
        ? AutomationDraftingProcess.getQuestionsWording(payload.inputs[questionAnswersInput])
        : submitted === proposalCardAction
        ? AutomationDraftingProcess.getCandidateWording(runtime, payload.inputs[candidateInput], t)
        : AutomationDraftingProcess.getScheduleWording(payload.inputs[scheduleInput], t);
    if (wording) {
      const item = new CopilotConversationItem();
      item.type = CopilotConversionItemType.User;
      item.question = wording;
      runtime.conversation.thread.push(item);
    }
    runtime.setConversation(runtime.conversation.clone());

    return true;
  }

  static applyProcessState(
    runtime: CopilotPanelRuntime,
    state: CopilotPanelProcessState,
  ): CopilotPreviewContent | undefined {
    if (!(state.data instanceof TenantProcessStateAutomationDrafting)) return undefined;

    AutomationDraftingProcess.getPanelState(runtime)?.setState(state.data);
    runtime.setConversation(state.conversation);

    return state.data.draft?.proposal ? CopilotPreviewContent.Usage : undefined;
  }

  static clearPreview(runtime: CopilotPanelRuntime): void {
    AutomationDraftingProcess.getPanelState(runtime)?.setState(undefined);
  }

  private static applySubmittedInputs(
    runtime: CopilotPanelRuntime,
    blockId: string | undefined,
    inputs: Record<string, string>,
  ): void {
    if (!blockId) return;

    for (const threadItem of runtime.conversation.thread) {
      const target = threadItem.contentBlocks?.find((candidate) => candidate.id === blockId);
      if (!target) continue;

      target.adaptiveInputs = { ...(target.adaptiveInputs ?? {}), ...inputs };

      return;
    }
  }

  private static getCandidateWording(
    runtime: CopilotPanelRuntime,
    choice: string | undefined,
    t: TFunction<string[]>,
  ): string | undefined {
    if (!choice) return undefined;
    if (choice === freshChoice) return t('copilot:AutomationDrafting.StartFresh');

    const candidate = AutomationDraftingProcess.getState(runtime)?.candidates.find((item) => item.id === choice);
    if (!candidate) return undefined;

    return t('copilot:AutomationDrafting.Chosen', { title: candidate.title });
  }

  //the user bubble of the answered question card (phase 32): the same lines the drafter's exchange keeps
  private static getQuestionsWording(answersJson: string | undefined): string | undefined {
    const answers = parseQuestionAnswersJson(answersJson);

    return answers && answers.length > 0 ? describeQuestionAnswers(answers) : undefined;
  }

  private static getScheduleWording(choice: string | undefined, t: TFunction<string[]>): string | undefined {
    switch (choice) {
      case 'weeklySaturday':
        return t('copilot:AutomationDrafting.SchedulePicked.WeeklySaturday');
      case 'monthlyFirst':
        return t('copilot:AutomationDrafting.SchedulePicked.MonthlyFirst');
      case 'halfYearlyFirst':
        return t('copilot:AutomationDrafting.SchedulePicked.HalfYearlyFirst');
      case 'none':
        return t('copilot:AutomationDrafting.SchedulePicked.None');
      default:
        return undefined;
    }
  }
}
